// Shared with the runtime resolver so the ruler cannot claim a range the
// published site would not serve.

const DEFAULT_ALIAS = "default";
// Absolute minimum previewable width — also the ruler's hard left bound and the
// floor the smallest breakpoint's range extends down to.
const MIN_PREVIEW_WIDTH_PX = 350;
// Absolute maximum previewable width — also the ruler's hard right bound and the
// ceiling the largest breakpoint's range extends to (in place of Infinity).
const MAX_PREVIEW_WIDTH_PX = 2500;

function toSortedBreakpoints(breakpoints) {
  return [...breakpoints ?? []]
      .map(breakpoint => ({ ...breakpoint, numericValue: parseInt(breakpoint?.value, 10) }))
      .filter(breakpoint => Number.isFinite(breakpoint.numericValue))
      .sort((left, right) => left.numericValue - right.numericValue);
}

function isMobileFirst(designVariant) {
  return designVariant === "mobile";
}

function resolveBreakpointForWidth(width, breakpoints, designVariant) {
  const sorted = toSortedBreakpoints(breakpoints);
  const matched = [...sorted].reverse().find(breakpoint => breakpoint.numericValue <= width);
  if (matched) {
    return matched;
  }
  if (isMobileFirst(designVariant)) {
    return null;
  }
  return sorted[0] ?? null;
}

// `Default` is absent by design: it is the options tree's inheritance base,
// not a viewport.
function breakpointRanges(breakpoints, designVariant) {
  const sorted = toSortedBreakpoints(breakpoints);
  return sorted.map((breakpoint, index) => {
    const next = sorted[index + 1];
    const isSmallest = index === 0;
    const claimsEverythingBelow = isSmallest && !isMobileFirst(designVariant);
    return {
      alias: breakpoint.alias,
      label: breakpoint.label,
      value: breakpoint.numericValue,
      min: claimsEverythingBelow ? 0 : breakpoint.numericValue,
      max: next ? next.numericValue - 1 : MAX_PREVIEW_WIDTH_PX,
    };
  });
}

function rangeForAlias(breakpoints, designVariant, alias) {
  return breakpointRanges(breakpoints, designVariant).find(range => range.alias === alias) ?? null;
}

function warnOnInvertedBounds(context) {
  // eslint-disable-next-line no-console
  console.error(
    "breakpoint drag bounds would invert — the ceiling is below the segment's own floor",
    context,
  );
}

// Only the top segment is unbounded, so only it takes `maxPreviewWidth`; clamping
// a bounded one to the screen inverted its bounds as you zoomed in.
function draggableBoundsForAlias(breakpoints, designVariant, alias, maxPreviewWidth) {
  const range = rangeForAlias(breakpoints, designVariant, alias);
  if (!range) {
    return null;
  }

  const floor = Math.max(range.min, MIN_PREVIEW_WIDTH_PX);
  const ceiling = Number.isFinite(range.max) ? range.max : maxPreviewWidth ?? MAX_PREVIEW_WIDTH_PX;
  if (ceiling < floor) {
    warnOnInvertedBounds({ alias, designVariant, floor, ceiling, maxPreviewWidth });
  }

  return { min: floor, max: Math.max(ceiling, floor) };
}

function clampWidthToRange(width, bounds) {
  if (!bounds) {
    return width;
  }
  return Math.min(Math.max(Math.round(width), bounds.min), bounds.max);
}

function isDefaultAlias(alias) {
  return !alias || alias === DEFAULT_ALIAS;
}

// Ruler marks for one breakpoint frame: the 0 / min / max / now positions (px)
// and the two gray bands (0→min and now→max). `now` defaults to full width
// (max) and is clamped into [min, max]. Everything is clamped to the absolute
// [380, 2500] bounds.
//   - Default frame, mobile-first: min = 380, max = smallest breakpoint's value.
//   - Default frame, desktop-first: min = largest breakpoint's value, max = 2500.
//   - Any other breakpoint (own value + its ordered neighbour): mobile-first
//     min = own, max = next-larger; desktop-first min = next-smaller, max = own.
function rulerMarksFor({ alias, breakpoints, designVariant, nowWidth } = {}) {
  const sorted = toSortedBreakpoints(breakpoints);
  const mobileFirst = isMobileFirst(designVariant);
  let min;
  let max;
  if (isDefaultAlias(alias)) {
    if (mobileFirst) {
      min = MIN_PREVIEW_WIDTH_PX;
      max = sorted.length ? sorted[0].numericValue : MAX_PREVIEW_WIDTH_PX;
    } else {
      min = sorted.length ? sorted[sorted.length - 1].numericValue : MIN_PREVIEW_WIDTH_PX;
      max = MAX_PREVIEW_WIDTH_PX;
    }
  } else {
    const index = sorted.findIndex(breakpoint => breakpoint.alias === alias);
    if (index < 0) {
      min = MIN_PREVIEW_WIDTH_PX;
      max = MAX_PREVIEW_WIDTH_PX;
    } else if (mobileFirst) {
      min = sorted[index].numericValue;
      max = sorted[index + 1] ? sorted[index + 1].numericValue : MAX_PREVIEW_WIDTH_PX;
    } else {
      min = sorted[index - 1] ? sorted[index - 1].numericValue : MIN_PREVIEW_WIDTH_PX;
      max = sorted[index].numericValue;
    }
  }
  min = Math.min(Math.max(min, MIN_PREVIEW_WIDTH_PX), MAX_PREVIEW_WIDTH_PX);
  max = Math.max(Math.min(max, MAX_PREVIEW_WIDTH_PX), min);
  const bounds = { min, max };
  const now = clampWidthToRange(Number.isFinite(nowWidth) ? nowWidth : max, bounds);
  return { zero: 0, min, max, now, bounds, grayBands: [[0, min], [now, max]] };
}

module.exports = {
  DEFAULT_ALIAS,
  MIN_PREVIEW_WIDTH_PX,
  MAX_PREVIEW_WIDTH_PX,
  resolveBreakpointForWidth,
  breakpointRanges,
  rangeForAlias,
  draggableBoundsForAlias,
  clampWidthToRange,
  isDefaultAlias,
  rulerMarksFor,
};
